import React from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { useMount } from "react-use";
import { Link as LinkIcon } from "@mui/icons-material";
import { Button } from "@mui/material";

import analytics from "fond/analytics";
import { useGetVersionQuery } from "fond/api";
import { FOND_VIRAL_DESIGN_PREFIX } from "fond/constants";
import useEntityViews from "fond/hooks/useEntityViews";
import { PageContent } from "fond/widgets";
import SkeletonLoadingGrid from "fond/widgets/SkeletonLoadingGrid";

import { DEFAULT_REPORT_LOADING_SKELETON, Page } from "../constants";
import { usePollReportStatusHandle, useReportData } from "../hooks";
import Navigation from "../Navigation";
import { reportWindowTitle } from "../util";

import ReportOverviewPageContent from "./ReportOverviewPageContent";

const ReportOverview: React.FC = () => {
  useEntityViews();
  const { reportId } = useParams<"reportId">();

  const { report, isLoading } = useReportData(reportId);
  usePollReportStatusHandle(report);

  const { data: version } = useGetVersionQuery(report?.Version?.ID ?? "", { skip: !report?.Version?.ID });

  useMount(() => {
    if (reportId) {
      analytics.track("Report.Overview.Overview viewed", { reportId });
    }

    if (report?.Name.startsWith(FOND_VIRAL_DESIGN_PREFIX) && reportId) {
      analytics.track("A viral design report is viewed", { reportId, reportName: report.Name });
    }
  });

  return (
    <PageContent
      windowTitle={reportWindowTitle("Overview", report?.Name)}
      title="Overview"
      action={
        version?.Project && (
          <Button component={Link} to={`/project/${version.Project}`} startIcon={<LinkIcon />}>
            Design project
          </Button>
        )
      }
      mainContent={
        !isLoading && report && report.Status !== null ? (
          <ReportOverviewPageContent report={report} />
        ) : (
          <SkeletonLoadingGrid items={DEFAULT_REPORT_LOADING_SKELETON} />
        )
      }
      navigation={<Navigation page={Page.OVERVIEW} />}
      testId="report-overview-page"
    />
  );
};

export default ReportOverview;
